import { useAppDispatch } from "hooks/hooks";
import {
  getExemptions,
  selectState,
  setSearchExemptionPopover
} from "../output.slice";
import SearchExemptionPopover from "./SearchExemptionPopover";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import PrintOutput from "./PrintOutput";
import { useNavigate } from "react-router-dom";
import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import Popover from "components/Popover/Popover";
import { useSelector } from "react-redux";
import { Control, useForm, UseFormGetValues, UseFormHandleSubmit, UseFormRegister, UseFormReset, UseFormSetFocus, UseFormSetValue } from "react-hook-form";
import { IExemptionSearch } from "Dashboard/scenes/Consumables/domain/IExemptionSearch";
import { SearchType } from "Dashboard/domain/SearchType";
import { usePagePrint } from "hooks/usePagePrint";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { useLocalStorage } from "hooks/LocalStorage";

interface IProps {
  defaultGetOutputListItemsSearchParams: IExemptionSearch;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IExemptionSearch>>;
  registerSearch: UseFormRegister<IExemptionSearch>;
  handleSubmitSearch: UseFormHandleSubmit<IExemptionSearch>;
  resetSearch: UseFormReset<IExemptionSearch>;
  controlSearch: Control<IExemptionSearch, any>;
  setFocusSearch: UseFormSetFocus<IExemptionSearch>;
  getValuesSearch: UseFormGetValues<IExemptionSearch>;
  setValueSearch: UseFormSetValue<IExemptionSearch>;
};

const Toolbox = ({
  defaultGetOutputListItemsSearchParams,
  setGetOutputListItemsSearchParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  setFocusSearch,
  getValuesSearch,
  setValueSearch,
}: IProps) => {
  const dispatch = useAppDispatch();
  const outputComponentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { searchExemptionPopover } = useSelector(selectState);

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("consumablesOutputSearchQuery", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("consumablesOutputSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("consumablesOutputSearchInputs", null);

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch());

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetOutputListItemsSearchParams({ searchTerm: storedSearchTerm });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchQuery = undefined;
      setGetOutputListItemsSearchParams(storedSearchInputs);
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchTerm", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchTypeValue(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchTerm") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    const currentSearchTerm = getValuesSearch("searchTerm");
    const currentSearchInputs = getValuesSearch();
    setStoredSearchTerm(currentSearchTerm);
    setStoredSearchType(searchTypeValue);
    setStoredSearchInputs(currentSearchInputs);
    navigate(path);
  };

  const handlePrintOutput = usePagePrint(
    outputComponentRef, { landscape: true, margins: "0.5cm" });

  return (
    <>
      <nav className="container">
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Back}
            tooltip="Potrošni materijali - Ulaz"
            toolbox={true}
            onClick={() => handleNavigation("/consumables")}
          />
        </div>
        <div className="output-nav-buttons">
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() => {
              dispatch(setSearchExemptionPopover({ isOpen: true }));
            }}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => {
                setGetOutputListItemsSearchParams(defaultGetOutputListItemsSearchParams);
                resetSearch();
              }}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampa"
            toolbox={true}
            onClick={handlePrintOutput}
          />
          <div className="print-area">
            <div ref={outputComponentRef}>
              <PrintOutput />
            </div>
          </div>
        </div>
        <Popover
          isOpen={searchExemptionPopover.isOpen}
          onClose={() => {
            dispatch(setSearchExemptionPopover({ isOpen: false }));
          }}
          title="Pretraga"
          direction="right"
          arrowStyle={{ top: "42px" }}
          position={{ top: 10, left: 190 }}
        >
          <SearchExemptionPopover
            handleSubmit={handleSubmitSearch}
            register={registerSearch}
            control={controlSearch}
            getValues={getValuesSearch}
            reset={resetSearch}
            setFocus={setFocusSearch}
            searchTypeValue={searchTypeValue}
            setSearchTypeValue={setSearchTypeValue}
            setValue={setValueSearch}
            setGetOutputListItemsSearchParams={setGetOutputListItemsSearchParams}
          />
        </Popover>
      </nav>
    </>
  );
};

export default Toolbox;
